import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  formatStepHash,
  parseStepHash,
  type StepSelection,
} from "@/lib/stepPanel";

/** The selected step lives in the URL hash (#run=<attempt>&step=<n>) so a
 *  link opens the panel. One module-level store: only one panel is open on
 *  the page at a time, whichever attempt it belongs to. Writes use
 *  replaceState so selecting never adds history entries or scrolls. */
const listeners = new Set<() => void>();
let cached: { hash: string; sel: StepSelection } = {
  hash: "",
  sel: { run: null, step: null },
};

function snapshot(): StepSelection {
  const hash = typeof window === "undefined" ? "" : window.location.hash;
  if (hash !== cached.hash) cached = { hash, sel: parseStepHash(hash) };
  return cached.sel;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  window.addEventListener("hashchange", cb);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("hashchange", cb);
  };
}

function write(sel: StepSelection): void {
  const url = `${window.location.pathname}${window.location.search}${formatStepHash(sel)}`;
  window.history.replaceState(window.history.state, "", url);
  for (const l of listeners) l();
}

export function useStepSelection(run: string): {
  step: string | null;
  select: (step: string | null) => void;
} {
  const sel = useSyncExternalStore(subscribe, snapshot, () => ({
    run: null,
    step: null,
  }));
  const step = sel.run === run ? sel.step : null;
  const select = useCallback(
    (s: string | null) => write({ run: s === null ? null : run, step: s }),
    [run],
  );
  return { step, select };
}

/** true at >= 768px (Tailwind `md`): side panel; below it a bottom sheet. */
export function useIsDesktop(): boolean {
  const q = "(min-width: 768px)";
  const [v, setV] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia(q).matches,
  );
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setV(m.matches);
    on();
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return v;
}
